import { z } from "zod";
import { PatientMessagingConsentSchema, PatientMessagingOfferedChannelsSchema, } from "./patient-messaging-consent.js";
import { IanaTimeZoneSchema, IdempotencyKeySchema, IsoDateTimeSchema } from "./primitives.js";
/**
 * Patient messages (PA-7 / EXECDEV-185): the texts (Twilio SMS) and emails (AWS SES) a clinic
 * sends a patient about a booking. Every message is one row of one outbox
 * (scheduling.patient_messages), sent by one worker lane (WORKER_RUNTIME=messaging), with its
 * delivery state from the provider (Twilio status callbacks; SES acceptance). Staff routes, on the
 * signed-in member's site:
 *
 * - GET  /v1/scheduling/appointments/:id/messages              AppointmentPatientMessagesSchema
 * - POST /v1/scheduling/appointments/:id/messages/resend       ResendPatientMessage*
 * - POST /v1/scheduling/appointments/:id/messages/acknowledge  AcknowledgePatientMessage*
 * - POST /v1/scheduling/appointments/:id/contact               CorrectSchedulingBookingContact*
 *                                                              (scheduling.ts)
 * - GET  /v1/scheduling/messages/attention                     PatientMessageAttentionListSchema
 * - GET  /v1/patients/:id/messaging                            PatientMessagingChartSchema
 *
 * The schedule read carries only each booking's message state (DemoAppointmentSchema
 * .messageAttention), so the schedule poll names no message. Every other read is audited.
 *
 * Privacy, by shape: no read carries a message body, a link or token, a full phone number or a
 * full email address. Recipients are masked (PatientMessageRecipientMaskSchema); the store keeps
 * only the mask and a per-clinic hash. A message never carries the reason for the visit. PA-7's
 * built-in bodies (default-v1) hold the clinic, the date and time, a link and the clinic phone;
 * from PA-8 a clinic's own wording (patient-message-templates.ts) may also name the patient, the
 * physician, the visit type or the clinic address, with a caution that its notice must say so.
 *
 * Who receives it (owner decision O4): the phone or email on the booking (what the patient typed,
 * or staff entered), even after the booking is linked to a chart. Staff correct a wrong one with
 * CorrectSchedulingBookingContactRequestSchema; queued messages then go to the corrected contact,
 * because the recipient is read when the message is sent, never when it is queued.
 *
 * This file imports nothing from the scheduling modules at runtime (types only), so
 * demo-scheduling.ts and scheduling.ts import it and never the other way round.
 */
// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------
/**
 * What a message is for. confirmation: the booking was made (online, or by staff with "Send
 * confirmation"). reminder: ahead of the visit (PA-8 plans them). intake_link and manage_link: a
 * link texted or emailed on its own (PA-8's desk "Text link"). cancellation and reschedule (PA-9,
 * lead decision L6): the booking was cancelled, or moved to another time, by the patient or by
 * staff, while the clinic's notice of that kind is on (PatientMessagingSettings cancellation /
 * reschedule). Each kind but manage_link is written with the clinic's template series of the
 * same name (patientMessageTemplateKind); manage_link keeps the built-in wording.
 */
export const PATIENT_MESSAGE_KINDS = [
    "confirmation",
    "reminder",
    "intake_link",
    "manage_link",
    "cancellation",
    "reschedule",
    "document_ready",
];
export const PatientMessageKindSchema = z.enum(PATIENT_MESSAGE_KINDS);
/** sms goes through Twilio, email through AWS SES in ca-central-1. */
export const PatientMessageChannelSchema = z.enum(["sms", "email"]);
/**
 * A message's state. Staff see every one; nothing is collapsed.
 *
 * - withheld: never attempted, by rule; the reason says which. Final.
 * - queued: waiting to be sent at scheduledFor (quiet hours may have moved it later).
 * - sending: the worker holds it and is handing it to the provider now.
 * - uncertain: the provider may have taken it (a timeout or server error after the request was
 *   written). Never sent again automatically: a status callback settles it, or it fails with
 *   reason unconfirmed after PATIENT_MESSAGING_LIMITS.uncertainMinutes.
 * - sent: the provider accepted it. Final for email in PA-7 (SES acceptance); a text moves on.
 * - delivered: the carrier confirmed delivery (texts). Final.
 * - failed: not delivered; the reason says why. Final.
 * - cancelled: the booking changed before it was sent (reason appointment_changed). Final.
 * - stopped: the number replied STOP to this clinic, so nothing was sent (reason opted_out).
 *   Final.
 */
export const PATIENT_MESSAGE_STATUSES = [
    "withheld",
    "queued",
    "sending",
    "uncertain",
    "sent",
    "delivered",
    "failed",
    "cancelled",
    "stopped",
];
export const PatientMessageStatusSchema = z.enum(PATIENT_MESSAGE_STATUSES);
/**
 * States in which a message may still go out, or may already have: a resend of the same kind and
 * channel is refused while one exists (message_in_flight), so two clicks never send twice.
 */
export const PATIENT_MESSAGE_IN_FLIGHT_STATUSES = new Set([
    "queued",
    "sending",
    "uncertain",
]);
/** Why a message was withheld: never attempted. */
const WITHHELD_REASONS = [
    /** The clinic does not send messages: no sender for the channel, or messaging is not offered. */
    "not_offered",
    /** The clinic turned this channel off (PA-8 messaging settings sms / email). */
    "channel_off",
    /**
     * The clinic turned this kind off (PA-8 messaging settings confirmation / reminders; PA-9
     * cancellation / reschedule).
     */
    "kind_off",
    /** The patient has not agreed to messages, or said no. */
    "no_consent",
    /** The booking has no phone (texts) or email (emails). */
    "no_recipient",
    /**
     * The patient agreed, but the booking's phone is not a North American number that can take a
     * text, or its email is not an address. Staff correct the contact, then resend.
     */
    "invalid_recipient",
];
/** Why a message failed. */
const FAILED_REASONS = [
    /** It could not be sent before its deadline (the lane was down or blocked, or quiet hours). */
    "expired",
    /** Texts: the number is not a valid phone number (Twilio 21211). */
    "invalid_number",
    /** Texts: the number cannot take texts, such as a landline (Twilio 21614, 30006). */
    "not_mobile",
    /** Texts: the phone was off or out of reach, or the number is not in service (30003, 30005). */
    "unreachable",
    /** Texts: the carrier filtered the message (30007). */
    "carrier_filtered",
    /** Texts: the message was blocked for this number (30004). */
    "blocked",
    /** The provider refused it for another reason (another 4xx; SES MessageRejected). */
    "provider_rejected",
    /** The clinic's sender is misconfigured (Twilio credentials or service; SES identity). */
    "configuration",
    /** A test lane may send only to its allow-listed test recipients; this one is not on it. */
    "policy_blocked",
    /** The provider kept answering "not now" (rate limits) until the attempts ran out. */
    "retry_exhausted",
    /** It was uncertain, and no status confirmed it within the time allowed. */
    "unconfirmed",
];
const CANCELLED_REASONS = [
    /** The booking was cancelled, moved, or the patient arrived before it was sent. */
    "appointment_changed",
];
const STOPPED_REASONS = [
    /** The number replied STOP to this clinic (or the provider reports it opted out). */
    "opted_out",
];
/** The reasons each status may carry. A status with no reasons carries none. */
export const PATIENT_MESSAGE_STATUS_REASONS = {
    withheld: WITHHELD_REASONS,
    queued: [],
    sending: [],
    uncertain: [],
    sent: [],
    delivered: [],
    failed: FAILED_REASONS,
    cancelled: CANCELLED_REASONS,
    stopped: STOPPED_REASONS,
};
/** A fixed, PHI-free vocabulary. Provider error text is never kept: it can hold the number. */
export const PatientMessageReasonSchema = z.enum([
    ...WITHHELD_REASONS,
    ...FAILED_REASONS,
    ...CANCELLED_REASONS,
    ...STOPPED_REASONS,
]);
/** Reasons only a text can have. */
const SMS_ONLY_REASONS = new Set([
    "invalid_number",
    "not_mobile",
    "unreachable",
    "carrier_filtered",
    "blocked",
]);
/** Whether a status, channel and reason belong together. */
export function patientMessageReasonFits(status, channel, reason) {
    const reasons = PATIENT_MESSAGE_STATUS_REASONS[status];
    if (reason === null)
        return reasons.length === 0;
    return reasons.includes(reason) && (channel === "sms" || !SMS_ONLY_REASONS.has(reason));
}
/**
 * Whether a message needs a person: it failed, or the patient agreed but the booking's contact
 * cannot be used. Withheld for any other reason, cancelled and stopped are the rules working.
 */
export function patientMessageNeedsPerson(status, reason) {
    return status === "failed" || (status === "withheld" && reason === "invalid_recipient");
}
function refineReason(value, context) {
    if (!patientMessageReasonFits(value.status, value.channel, value.reason)) {
        context.addIssue({
            code: "custom",
            message: "The reason must be one this status and channel can have",
            path: ["reason"],
        });
    }
}
/**
 * How a message went out: simulated (no provider was called; synthetic data only) or live (the
 * channel's provider: Twilio for texts, SES for emails).
 */
export const PatientMessageTransportSchema = z.enum(["simulated", "live"]);
/**
 * Which wording a message used, recorded when it is sent: the outbox's template_key and
 * template_version, so the store shows which text went out without keeping the body. PA-7's
 * built-in bodies are PATIENT_MESSAGE_DEFAULT_TEMPLATE ("default-v1"); PA-8's clinic templates
 * add their own keys and versions.
 */
export const PatientMessageTemplateKeySchema = z.string().regex(/^[a-z][a-z0-9_]{0,39}$/);
export const PatientMessageTemplateRefSchema = z
    .object({
    key: PatientMessageTemplateKeySchema,
    version: z.number().int().positive().max(2_147_483_647),
})
    .strict();
export const PATIENT_MESSAGE_DEFAULT_TEMPLATE = {
    key: "default",
    version: 1,
};
/** "default-v1": how a template is named in docs and the staff panel. */
export function patientMessageTemplateLabel(template) {
    return `${template.key}-v${template.version}`;
}
// ---------------------------------------------------------------------------
// Rules each kind follows
// ---------------------------------------------------------------------------
/** The page a message's link opens: the questionnaire, or the appointment's manage page. */
export const PatientMessageLinkPurposeSchema = z.enum(["intake", "manage", "document"]);
export function patientMessageLinkPurpose(kind, intakeStatus) {
    switch (kind) {
        case "document_ready":
            return "document";
        case "intake_link":
            return "intake";
        case "manage_link":
            return "manage";
        case "confirmation":
        case "reminder":
        case "reschedule":
            return intakeStatus === "required" || intakeStatus === "sent" ? "intake" : "manage";
        case "cancellation":
            return null;
    }
}
/** Whether a kind's message carries a link (every kind but a cancellation notice). */
export function patientMessageKindCarriesLink(kind) {
    return kind !== "cancellation";
}
/**
 * The clinic-local window in which texts and emails that can wait are held (lead decision J5):
 * from PA-8 the default of each clinic's own quietHours setting (lead decision K6;
 * PATIENT_MESSAGING_DEFAULT_SETTINGS), which the clinic may change or turn off.
 */
export const PATIENT_MESSAGE_QUIET_HOURS = { start: "21:00", end: "08:00" };
/**
 * The kind-aware relevance rule the store applies when a message is claimed and again just
 * before it is sent, and that its cancel trigger follows: a queued message whose appointment left
 * the kind's statuses (or, where it says so, moved) is cancelled with reason appointment_changed;
 * one already being sent is never touched. Later kinds bring their own entry, so the worker and
 * the trigger never change for them: PA-9's cancellation notice is relevant once cancelled, and
 * its change-of-time notice while the booking is ahead at the time it names (a later move
 * cancels it and plans another).
 *
 * Quiet hours hold reminders only: a confirmation answers the patient's own booking, a link
 * text answers a patient at the desk (J5), and a cancellation or change-of-time notice tells the
 * patient of a change at once, since waiting until morning could be too late for the visit. A
 * staff resend follows its kind's rule.
 */
export const PATIENT_MESSAGE_KIND_RULES = {
    confirmation: {
        appointmentStatuses: ["booked", "confirmed"],
        cancelOnReschedule: true,
        quietHours: false,
    },
    reminder: {
        appointmentStatuses: ["booked", "confirmed"],
        cancelOnReschedule: true,
        quietHours: true,
    },
    intake_link: {
        appointmentStatuses: ["booked", "confirmed", "checked_in", "in_progress"],
        cancelOnReschedule: false,
        quietHours: false,
    },
    manage_link: {
        appointmentStatuses: ["booked", "confirmed"],
        cancelOnReschedule: false,
        quietHours: false,
    },
    cancellation: {
        appointmentStatuses: ["cancelled"],
        cancelOnReschedule: false,
        quietHours: false,
    },
    reschedule: {
        appointmentStatuses: ["booked", "confirmed"],
        cancelOnReschedule: true,
        quietHours: false,
    },
    document_ready: { appointmentStatuses: [], cancelOnReschedule: false, quietHours: false },
};
/** Totals and timings the store, the worker and the reads share. */
export const PATIENT_MESSAGING_LIMITS = {
    /** Messages one appointment read returns, newest first. */
    appointmentMessages: 50,
    /** Rows the Attention read returns. */
    attentionItems: 200,
    /** Phone numbers and email addresses the chart card lists. */
    chartRecipients: 10,
    /** Staff resends per appointment, across kinds and channels (message_resend_limit). */
    resendsPerAppointment: 5,
    /**
     * Attempts before a message fails with retry_exhausted. Only an explicit "not accepted" answer
     * is retried (HTTP 429, Twilio 20429, SES Throttling or TooManyRequests), or a failure before
     * the request was written (no connection). Any other server error, a timeout or a reset after
     * the request was written may have reached the provider, which takes no idempotency key: the
     * message is uncertain and never sent again automatically.
     */
    sendAttempts: 5,
    /** The first retry waits this long; each next one twice as long, up to retryMaxSeconds. */
    retryBaseSeconds: 30,
    retryMaxSeconds: 900,
    /** An uncertain message fails with reason unconfirmed when nothing settles it in this time. */
    uncertainMinutes: 30,
    /** A confirmation not sent within this of booking (or by the start, if sooner) expires. */
    confirmationExpiryMinutes: 120,
    /** A questionnaire link in a message lasts this long, and never past the appointment start. */
    intakeLinkDays: 7,
    /** At most one auto-reply to a number, per clinic, in this many hours (owner decision O3). */
    autoReplyHours: 24,
};
// ---------------------------------------------------------------------------
// Recipients
// ---------------------------------------------------------------------------
/** NANP: area code and exchange each start 2-9 and are not N11 (211, 311 ... 911). */
const NANP_NATIONAL_NUMBER = /^(?!\d11)[2-9]\d{2}(?!\d11)[2-9]\d{6}$/;
/**
 * A booking phone as the E.164 number a text goes to (+1 and 10 digits), or null when it is not a
 * North American number. Like the store's private.scheduling_phone_digits, every non-digit is
 * dropped and a leading 1 on 11 digits is the country code; anything else (an extension, a
 * number from elsewhere) is null, and a text to it is withheld with reason invalid_recipient.
 * Twilio's geographic permissions keep texts to Canada.
 */
export function normalizePatientMessagePhone(value) {
    const digits = value.replace(/\D/g, "");
    const national = digits.length === 10
        ? digits
        : digits.length === 11 && digits.startsWith("1")
            ? digits.slice(1)
            : null;
    return national !== null && NANP_NATIONAL_NUMBER.test(national) ? `+1${national}` : null;
}
/**
 * The one email rule for messages, the same as the store's private.patient_message_email_valid:
 * an address the booking contract takes (zod's email pattern) whose local part is at most 64
 * characters and whose domain labels are DNS names (1-63 characters, not starting or ending in
 * "-"), at most 254 characters in all. SES refuses the rest, so the store withholds a booking
 * email outside it with reason invalid_recipient before any link is minted, and the lane (which
 * checks it again just before a send) never meets one the store let through.
 */
const PATIENT_MESSAGE_EMAIL = /^(?=[^@]{1,64}@)(?!\.)(?!.*\.\.)[-A-Za-z0-9_'+.]*[A-Za-z0-9_+-]@(?:[A-Za-z0-9](?:[-A-Za-z0-9]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/;
/** Whether an email address (as stored: trimmed) can receive a patient message. */
export function isPatientMessageEmail(value) {
    return value.length <= 254 && PATIENT_MESSAGE_EMAIL.test(value);
}
/** "***-***-0187": the last four digits only, or null with fewer than four digits. */
export function maskPatientMessagePhone(value) {
    const digits = value.replace(/\D/g, "");
    return digits.length >= 4 ? `***-***-${digits.slice(-4)}` : null;
}
/** "a***@example.test": the first character and the domain (lower case), or null. */
export function maskPatientMessageEmail(value) {
    const email = value.trim();
    const at = email.lastIndexOf("@");
    const first = Array.from(email.slice(0, Math.max(at, 0)))[0];
    const domain = email.slice(at + 1).toLowerCase();
    if (at <= 0 || first === undefined || /\s/u.test(first) || !/^[^\s@]{1,253}$/u.test(domain)) {
        return null;
    }
    return `${first}***@${domain}`;
}
const PHONE_MASK = /^\*{3}-\*{3}-\d{4}$/;
const EMAIL_MASK = /^[^\s@]\*{3}@[^\s@]{1,253}$/u;
/** A masked recipient as the helpers above make it: never a full number or address. */
export const PatientMessageRecipientMaskSchema = z
    .string()
    .max(300)
    .refine((value) => PHONE_MASK.test(value) || EMAIL_MASK.test(value), "A recipient is shown masked");
/** Whether a mask is the channel's kind: a phone mask for texts, an email mask for emails. */
export function patientMessageRecipientMaskFits(channel, masked) {
    return channel === "sms" ? PHONE_MASK.test(masked) : EMAIL_MASK.test(masked);
}
function refineRecipientMask(value, context) {
    if (value.recipientMasked !== null &&
        !patientMessageRecipientMaskFits(value.channel, value.recipientMasked)) {
        context.addIssue({
            code: "custom",
            message: "A text shows a masked phone number and an email a masked address",
            path: ["recipientMasked"],
        });
    }
}
// ---------------------------------------------------------------------------
// Staff: one appointment's messages
// ---------------------------------------------------------------------------
/** States in which the worker has handed the message to a transport, or is doing so. */
const DISPATCHED_STATUSES = new Set([
    "sending",
    "uncertain",
    "sent",
    "delivered",
]);
/**
 * One message as staff see it: what and how, never the body, the link or the full recipient.
 * Times are on the clinic's clock (the read's timeZone).
 */
export const PatientMessageSchema = z
    .object({
    id: z.string().uuid(),
    kind: PatientMessageKindSchema.exclude(["document_ready"]),
    channel: PatientMessageChannelSchema,
    status: PatientMessageStatusSchema,
    /** Present exactly for withheld, failed, cancelled and stopped. */
    reason: PatientMessageReasonSchema.nullable(),
    /** Null when the booking had no usable phone or email for the channel. */
    recipientMasked: PatientMessageRecipientMaskSchema.nullable(),
    /**
     * Set, together with template and linkPurpose, once the message was handed to a transport,
     * and always from sending on; null while it never was. A message that went back to queued
     * for a retry keeps its last attempt's values until the next attempt replaces them. A
     * cancellation notice carries no link, so its linkPurpose is always null
     * (patientMessageKindCarriesLink).
     */
    transport: PatientMessageTransportSchema.nullable(),
    template: PatientMessageTemplateRefSchema.nullable(),
    linkPurpose: PatientMessageLinkPurposeSchema.exclude(["document"]).nullable(),
    createdAt: IsoDateTimeSchema,
    /** When it is (or was) due; quiet hours may have moved it later than createdAt. */
    scheduledFor: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    /** When the provider accepted it. */
    sentAt: IsoDateTimeSchema.nullable(),
    /** When the carrier confirmed delivery; only on delivered. */
    deliveredAt: IsoDateTimeSchema.nullable(),
    /** The message a staff member resent, and who did; both null on a planned message. */
    resendOf: z.string().uuid().nullable(),
    /** The staff member's display name, or "A staff member"; never an email address. */
    resentByName: z.string().trim().min(1).max(200).nullable(),
    /** When a staff member marked the problem done; only on a message that needed a person. */
    acknowledgedAt: IsoDateTimeSchema.nullable(),
    /**
     * This message is the booking's Attention row: the latest of its kind and channel, it needs
     * a person (patientMessageNeedsPerson), nobody marked it done, and the booking is still ahead
     * (booked or confirmed). "Mark done" is offered on it.
     */
    needsAttention: z.boolean(),
    /**
     * Whether Resend would be taken now: the booking is booked or confirmed, nothing of this kind
     * and channel is in flight, the resend limit is not reached, and the clinic, the patient's
     * agreement and the recipient allow it. The server still decides (SchedulingErrorDetail).
     */
    canResend: z.boolean(),
})
    .strict()
    .superRefine((value, context) => {
    refineReason(value, context);
    refineRecipientMask(value, context);
    const carriesLink = patientMessageKindCarriesLink(value.kind);
    const sentWith = carriesLink
        ? [value.transport, value.template, value.linkPurpose]
        : [value.transport, value.template];
    if (!sentWith.every((field) => field === null) && !sentWith.every((field) => field !== null)) {
        context.addIssue({
            code: "custom",
            message: "A message sent says how, with which wording and which link, all together",
            path: ["transport"],
        });
    }
    if (!carriesLink && value.linkPurpose !== null) {
        context.addIssue({
            code: "custom",
            message: "A cancellation notice carries no link",
            path: ["linkPurpose"],
        });
    }
    if (DISPATCHED_STATUSES.has(value.status) && value.transport === null) {
        context.addIssue({
            code: "custom",
            message: "A message being sent or sent says how it went out",
            path: ["transport"],
        });
    }
    if ((value.status === "sent" || value.status === "delivered") && value.sentAt === null) {
        context.addIssue({
            code: "custom",
            message: "A sent message says when it was sent",
            path: ["sentAt"],
        });
    }
    if ((value.status === "delivered") !== (value.deliveredAt !== null)) {
        context.addIssue({
            code: "custom",
            message: "Only a delivered message says when it was delivered, and it always does",
            path: ["deliveredAt"],
        });
    }
    if ((value.resendOf === null) !== (value.resentByName === null)) {
        context.addIssue({
            code: "custom",
            message: "A resent message names who resent it, and only a resent one",
            path: ["resentByName"],
        });
    }
    const needsPerson = patientMessageNeedsPerson(value.status, value.reason);
    if (value.acknowledgedAt !== null && !needsPerson) {
        context.addIssue({
            code: "custom",
            message: "Only a message that needed a person is marked done",
            path: ["acknowledgedAt"],
        });
    }
    if (value.needsAttention && (!needsPerson || value.acknowledgedAt !== null)) {
        context.addIssue({
            code: "custom",
            message: "Only a problem nobody marked done needs attention",
            path: ["needsAttention"],
        });
    }
});
function refineUniqueMessages(messages, context) {
    const ids = messages.map((message) => message.id);
    if (new Set(ids).size !== ids.length) {
        context.addIssue({
            code: "custom",
            message: "Each message is listed once",
            path: ["messages"],
        });
    }
    const attention = messages
        .filter((message) => message.needsAttention)
        .map((message) => `${message.kind}:${message.channel}`);
    if (new Set(attention).size !== attention.length) {
        context.addIssue({
            code: "custom",
            message: "At most one message of each kind and channel needs attention",
            path: ["messages"],
        });
    }
}
/**
 * `GET /v1/scheduling/appointments/:appointmentId/messages`: the booking's messages, newest
 * first, up to the limit, and (PA-8) whether the clinic may text or email the patient about it:
 * the channels the clinic offers and each channel's state in the booking's scope (owner decision
 * O1: the chart's decisions once linked; a STOP from the booking's number). Queued messages
 * include the reminders planned for the visit. Audited with the booking's patient, so a client
 * fetches it when the panel opens, never on the schedule poll.
 */
export const AppointmentPatientMessagesSchema = z
    .object({
    timeZone: IanaTimeZoneSchema,
    offeredChannels: PatientMessagingOfferedChannelsSchema,
    consent: PatientMessagingConsentSchema,
    messages: z.array(PatientMessageSchema).max(PATIENT_MESSAGING_LIMITS.appointmentMessages),
})
    .strict()
    .superRefine((value, context) => refineUniqueMessages(value.messages, context));
/**
 * `POST /v1/scheduling/appointments/:appointmentId/messages/resend`: send a message of the
 * same kind and channel again, as a new message (resendOf names this one) with a fresh link, to
 * the booking's current contact. The key names one resend: an exact retry returns the message it
 * created (created false). Refusals carry a SchedulingErrorDetail: message_in_flight,
 * message_not_resendable, message_consent_missing, message_recipient_stopped,
 * message_recipient_missing, message_recipient_invalid, messaging_not_offered,
 * message_resend_limit. A key reused for another message is a plain conflict.
 */
export const ResendPatientMessageRequestSchema = z
    .object({ messageId: z.string().uuid(), idempotencyKey: IdempotencyKeySchema })
    .strict();
export const ResendPatientMessageResponseSchema = z
    .object({ created: z.boolean(), message: PatientMessageSchema })
    .strict()
    .refine((value) => value.message.resendOf !== null, {
    message: "A resend returns the new message",
    path: ["message", "resendOf"],
});
/**
 * `POST /v1/scheduling/appointments/:appointmentId/messages/acknowledge` ("Mark done"): a
 * person dealt with a message that needed one (they called the patient, say), so it leaves
 * Attention and the booking's row. Marking a message already done returns it unchanged; one that
 * no longer needs attention (it was resent, or the booking moved on) is refused with
 * message_changed.
 */
export const AcknowledgePatientMessageRequestSchema = z
    .object({ messageId: z.string().uuid() })
    .strict();
export const AcknowledgePatientMessageResponseSchema = z
    .object({ message: PatientMessageSchema })
    .strict()
    .refine((value) => value.message.acknowledgedAt !== null, {
    message: "A message marked done says when",
    path: ["message", "acknowledgedAt"],
});
// ---------------------------------------------------------------------------
// Staff: the schedule row and Attention
// ---------------------------------------------------------------------------
/** The message states a booking's row shows. Sent and delivered show only in the panel. */
export const SchedulingMessageAttentionStatusSchema = z.enum([
    "failed",
    "withheld",
    "uncertain",
    "stopped",
]);
/**
 * A booking's message state on the staff schedule read (DemoAppointmentSchema.messageAttention),
 * naming one message: among the latest message of each kind and channel, one that needs a person
 * and that nobody marked done (failed, or withheld with invalid_recipient) first; then status
 * stopped (reason opted_out, the row's quiet "Texts stopped") while the booking's current contact
 * on that channel is opted out at this clinic, naming a stopped message before one that went or is
 * going to that contact (so a STOP after delivery shows, and START or a corrected contact clears
 * it); then one that is uncertain (no pill: it settles by itself within 30 minutes). The most
 * recently updated breaks a tie. The schedule row shows it as its pill below every intake and
 * chart rule; opening it opens the booking's messages. reason is left out for uncertain.
 */
export const SchedulingMessageAttentionSchema = z
    .object({
    status: SchedulingMessageAttentionStatusSchema,
    kind: PatientMessageKindSchema.exclude(["document_ready"]),
    channel: PatientMessageChannelSchema,
    reason: PatientMessageReasonSchema.optional(),
})
    .strict()
    .superRefine((value, context) => {
    const reason = value.reason ?? null;
    if (!patientMessageReasonFits(value.status, value.channel, reason) ||
        (value.status === "withheld" && !patientMessageNeedsPerson(value.status, reason))) {
        context.addIssue({
            code: "custom",
            message: "A row shows a failure, an unusable contact, an uncertain message or a STOP",
            path: ["reason"],
        });
    }
});
/** Whether the row's message state asks something of staff (failed or an unusable contact). */
export function schedulingMessageNeedsPerson(attention) {
    return (attention !== undefined && (attention.status === "failed" || attention.status === "withheld"));
}
/**
 * Attention's own item-type filter value for these rows, never clinic messaging's "messages"
 * filter (internal conversations, offered only while clinic messaging is on).
 */
export const PATIENT_MESSAGE_ATTENTION_FILTER = "patient_messages";
/** The Attention URL search key that selects one of these rows by its message id. */
export const PATIENT_MESSAGE_ATTENTION_SEARCH_KEY = "patientMessage";
/** One Attention row: a message that needs a person (PatientMessage.needsAttention). */
export const PatientMessageAttentionItemSchema = z
    .object({
    messageId: z.string().uuid(),
    appointmentId: z.string().uuid(),
    /** The booking's chart, once linked; the row has no chart before that. */
    patientId: z.string().uuid().nullable(),
    /** The booking's name for the patient. */
    patientName: z.string().trim().min(1).max(240),
    startAt: IsoDateTimeSchema,
    kind: PatientMessageKindSchema.exclude(["document_ready"]),
    channel: PatientMessageChannelSchema,
    status: z.enum(["failed", "withheld"]),
    reason: PatientMessageReasonSchema,
    recipientMasked: PatientMessageRecipientMaskSchema.nullable(),
    /** When it failed (or was withheld). */
    updatedAt: IsoDateTimeSchema,
    canResend: z.boolean(),
})
    .strict()
    .superRefine((value, context) => {
    refineRecipientMask(value, context);
    if (!patientMessageReasonFits(value.status, value.channel, value.reason) ||
        !patientMessageNeedsPerson(value.status, value.reason)) {
        context.addIssue({
            code: "custom",
            message: "An Attention row is a failure or an unusable contact",
            path: ["reason"],
        });
    }
});
/**
 * `GET /v1/scheduling/messages/attention`: the site's messages that need a person, for bookings
 * still ahead, newest first. Ownerless rows: front desk and clinic admins see them as their own.
 * Audited with the count only. Clients ask only while the durable schedule lane is on.
 */
export const PatientMessageAttentionListSchema = z
    .object({
    timeZone: IanaTimeZoneSchema,
    items: z.array(PatientMessageAttentionItemSchema).max(PATIENT_MESSAGING_LIMITS.attentionItems),
})
    .strict()
    .superRefine((value, context) => {
    const ids = value.items.map((item) => item.messageId);
    if (new Set(ids).size !== ids.length) {
        context.addIssue({ code: "custom", message: "Each message is listed once", path: ["items"] });
    }
    const keys = value.items.map((item) => `${item.appointmentId}:${item.kind}:${item.channel}`);
    if (new Set(keys).size !== keys.length) {
        context.addIssue({
            code: "custom",
            message: "One row per booking, kind and channel",
            path: ["items"],
        });
    }
});
// ---------------------------------------------------------------------------
// Staff: the chart's "Text and email messages" card
// ---------------------------------------------------------------------------
/**
 * The latest change to whether a number takes this clinic's texts: the patient replied STOP or
 * START to the clinic's number, or the provider reported the number opted out (Twilio 21610 on a
 * send). Only the patient lifts a STOP, by replying START; staff cannot.
 */
export const PatientMessagingOptOutChangeSchema = z.enum([
    "reply_stop",
    "reply_start",
    "provider_opt_out",
]);
/**
 * One phone number or email address and whether this clinic may message it. A STOP is per
 * number per clinic: it stops texts to that number for every chart that uses it, so the card
 * says "texts to this number were stopped", never that the patient opted out of care.
 */
export const PatientMessagingRecipientSchema = z
    .object({
    /**
     * Its place on the card (1 first), unique in one answer: the list key. Two different numbers
     * can share a mask (the same last four digits), so the mask is not a key. It says nothing
     * about the number or address.
     */
    position: z.number().int().min(1).max(PATIENT_MESSAGING_LIMITS.chartRecipients),
    channel: PatientMessageChannelSchema,
    recipientMasked: PatientMessageRecipientMaskSchema,
    /** The chart's current phone or email; otherwise it is only on a booking of this patient. */
    onChart: z.boolean(),
    /**
     * When the latest visit of this patient's bookings here that use it starts, or null when no
     * booking does (only on the chart). Tells apart two booking contacts that share a mask.
     */
    lastVisitAt: IsoDateTimeSchema.nullable(),
    state: z.enum(["active", "stopped"]),
    change: PatientMessagingOptOutChangeSchema.nullable(),
    changedAt: IsoDateTimeSchema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (!patientMessageRecipientMaskFits(value.channel, value.recipientMasked)) {
        context.addIssue({
            code: "custom",
            message: "A text shows a masked phone number and an email a masked address",
            path: ["recipientMasked"],
        });
    }
    if ((value.change === null) !== (value.changedAt === null)) {
        context.addIssue({
            code: "custom",
            message: "A change says when it happened, and only a change",
            path: ["changedAt"],
        });
    }
    if (!value.onChart && value.lastVisitAt === null) {
        context.addIssue({
            code: "custom",
            message: "A contact only on a booking names that booking's visit",
            path: ["lastVisitAt"],
        });
    }
    const stoppedBy = value.change === "reply_stop" || value.change === "provider_opt_out";
    if ((value.state === "stopped") !== stoppedBy) {
        context.addIssue({
            code: "custom",
            message: "A number is stopped exactly when its latest change stopped it",
            path: ["state"],
        });
    }
});
/**
 * `GET /v1/patients/:patientId/messaging`: the chart's phone and email and the contacts on this
 * patient's bookings at this site, each with whether the clinic may message it, and (PA-8) the
 * channels the clinic offers and the chart's state per channel at this clinic (owner decision O1:
 * the latest decision on the chart or any of its bookings here; never stopped, which the
 * recipients show number by number). Audited with the patient id. Fetched when the chart's
 * consent details open, never polled.
 */
export const PatientMessagingChartSchema = z
    .object({
    timeZone: IanaTimeZoneSchema,
    offeredChannels: PatientMessagingOfferedChannelsSchema,
    consent: PatientMessagingConsentSchema,
    recipients: z
        .array(PatientMessagingRecipientSchema)
        .max(PATIENT_MESSAGING_LIMITS.chartRecipients),
})
    .strict()
    .superRefine((value, context) => {
    for (const channel of ["sms", "email"]) {
        if (value.consent[channel].state === "stopped") {
            context.addIssue({
                code: "custom",
                message: "A chart shows each number's STOP on its recipient row, not on the channel",
                path: ["consent", channel, "state"],
            });
        }
    }
    value.recipients.forEach((recipient, index) => {
        if (recipient.position !== index + 1) {
            context.addIssue({
                code: "custom",
                message: "Recipients are listed in position order, each position once",
                path: ["recipients", index, "position"],
            });
        }
    });
});
// ---------------------------------------------------------------------------
// The messaging lane: transport and health
// ---------------------------------------------------------------------------
/**
 * The lane's transport (PATIENT_MESSAGING_TRANSPORT_MODE). off: unset or "off"; nothing is
 * claimed. simulated: no provider is called (synthetic data only). live: the providers, only
 * inside the synthetic staging boundary with a test-recipient allow-list, or against loopback fake
 * providers with static fake credentials in development. blocked: live was asked for outside
 * those, or something it needs is missing; the lane stays up, only expires and gates messages,
 * and sends nothing.
 */
export const PatientMessagingTransportModeSchema = z.enum(["off", "simulated", "live", "blocked"]);
/** The staging health aggregate's word for each transport; none of them is "unavailable". */
export const PATIENT_MESSAGING_SERVICE_STATES = {
    off: "disabled",
    simulated: "simulated",
    live: "ok",
    blocked: "blocked",
};
/** The messaging lane's GET /healthz body: the worker health body plus the transport. */
export const PatientMessagingWorkerHealthSchema = z
    .object({
    status: z.enum(["ok", "stale"]),
    runtime: z.literal("messaging"),
    lastCycleAt: IsoDateTimeSchema,
    phiProcessing: z.literal(false),
    /** Present exactly when the transport is off: alive, with no work enabled. */
    disabled: z.literal(true).optional(),
    transport: PatientMessagingTransportModeSchema,
})
    .strict()
    .refine((value) => (value.disabled === true) === (value.transport === "off"), {
    message: "The lane is disabled exactly when its transport is off",
    path: ["disabled"],
});
/**
 * What one send attempt came to, as the worker reports it to the store. accepted: the provider
 * took it (sent). retry: an explicit "not now", or no request was written; it goes back to
 * queued with backoff (PATIENT_MESSAGING_LIMITS.sendAttempts). rejected: refused; failed with a
 * reason. uncertain: it may have reached the provider. stopped: the provider reports the number
 * opted out (Twilio 21610); an opt-out is recorded.
 */
export const PatientMessageSendOutcomeSchema = z.enum([
    "accepted",
    "retry",
    "rejected",
    "uncertain",
    "stopped",
]);
// ---------------------------------------------------------------------------
// Replies to the clinic's number (owner decision O3)
// ---------------------------------------------------------------------------
/**
 * What a text to the clinic's number asks. stop, start and help are Twilio's Advanced Opt-Out
 * keywords: Twilio answers them itself, and stop and start change the opt-out ledger. Anything
 * else gets the one auto-reply (patientMessagingAutoReply); its words are never stored, logged
 * or passed to the store.
 */
export const PatientMessagingReplyKeywordSchema = z.enum(["stop", "start", "help", "other"]);
/** Twilio's default single-word keywords (case-insensitive, the whole message, trimmed). */
export const PATIENT_MESSAGING_REPLY_KEYWORDS = {
    stop: ["STOP", "STOPALL", "UNSUBSCRIBE", "CANCEL", "END", "QUIT", "OPTOUT", "REVOKE"],
    start: ["START", "UNSTOP", "YES"],
    help: ["HELP", "INFO"],
};
/**
 * The keyword a reply is: Twilio's OptOutType (STOP, START or HELP) when it sent one, which also
 * covers keywords the clinic's Messaging Service adds; otherwise the whole trimmed message as one
 * of Twilio's default keywords. Everything else is other.
 */
export function patientMessagingReplyKeyword(reply) {
    const optOut = reply.optOutType?.trim().toUpperCase();
    if (optOut === "STOP")
        return "stop";
    if (optOut === "START")
        return "start";
    if (optOut === "HELP")
        return "help";
    const word = (reply.body ?? "").trim().toUpperCase();
    for (const keyword of ["stop", "start", "help"]) {
        if (PATIENT_MESSAGING_REPLY_KEYWORDS[keyword].includes(word)) {
            return keyword;
        }
    }
    return "other";
}
/**
 * The only answer to a reply that is not a keyword, at most once per number per clinic every
 * PATIENT_MESSAGING_LIMITS.autoReplyHours. Messages to the clinic's number are not read, so it
 * points the patient to the phone and to 911. Control and format characters in the clinic phone
 * are dropped.
 */
export function patientMessagingAutoReply(clinicPhone) {
    const phone = (clinicPhone ?? "")
        .replace(/[\p{Cc}\p{Cf}]/gu, " ")
        .replace(/\s+/g, " ")
        .trim();
    return phone === ""
        ? "This number doesn't take messages. Please call the clinic. For emergencies, call 911."
        : `This number doesn't take messages. Please call ${phone}. For emergencies, call 911.`;
}
//# sourceMappingURL=patient-messaging.js.map